import React, { useMemo, useState } from 'react';
import MetricsEditor from './MetricsEditor.jsx';
import StationEditor from './StationEditor.jsx';
import FullScreenSheet from '../FullScreenSheet.jsx';
import {
  SketchCheckbox,
  SketchDumbbell,
  SketchRunner,
  SketchUnderline,
} from '../sketch/index.jsx';
import { REPEAT_CHOICES, REPEAT_ONCE } from '../../utils/habits.js';
import { placementHint } from '../../utils/placement.js';
import {
  MAX_STATIONS,
  MODE_SINGLE,
  MODE_STATIONS,
  createSportConfig,
  createStation,
  normalizeSportConfig,
} from '../../utils/sport.js';

const MODES = [
  {
    value: MODE_STATIONS,
    label: 'stations',
    Icon: SketchDumbbell,
    hint: 'gym, calisthenics — several exercises, sets each',
  },
  {
    value: MODE_SINGLE,
    label: 'one step',
    Icon: SketchRunner,
    hint: 'running, swimming, cycling — logged once',
  },
];

// Same order as the inline habit line — see REPEAT_CHOICES in utils/habits.js.
const REPEATS = REPEAT_CHOICES;

/** Row of pill buttons — the app's standard way of picking one of a few. */
function PillChoice({ label, options, value, onChange }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map(({ value: optionValue, label: optionLabel, Icon }) => (
          <button
            key={optionValue}
            type="button"
            onClick={() => onChange(optionValue)}
            aria-pressed={value === optionValue}
            className={`flex items-center gap-1.5 rounded-pill border px-2.5 py-1 text-[10px]
                        uppercase tracking-widest transition-colors ${
                          value === optionValue
                            ? 'border-accent bg-accent text-white'
                            : 'border-line text-ink-soft hover:border-ink'
                        }`}
          >
            {Icon && <Icon />}
            {optionLabel}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Hand-drawn checkbox row.
 * A real <input type="checkbox"> stays in the markup, visually hidden, so
 * keyboard and screen-reader behaviour is the browser's rather than ours.
 */
function CheckRow({ checked, onChange, label, hint }) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="peer sr-only"
      />
      <SketchCheckbox
        checked={checked}
        className={`mt-0.5 transition-colors peer-focus-visible:text-accent ${
          checked ? 'text-accent' : 'text-ink-soft'
        }`}
      />
      <span className="min-w-0">
        <span className="block font-hand text-lg leading-tight">{label}</span>
        <span className="block font-hand text-base leading-tight text-ink-soft">{hint}</span>
      </span>
    </label>
  );
}

/**
 * Full-page modal for designing a sport activity.
 *
 * Two shapes:
 *  - stations — a list of exercises, each with sets, a rep target and its own
 *    numeric values (kg, rpe, …) recorded per set;
 *  - one step — a single activity with numeric values recorded once.
 *
 * Both produce the same blueprint shape (see utils/sport.js), so the session
 * screen only has one thing to render.
 *
 * The composer carries no date. It is opened from the header, and the finished
 * draft is handed back for the calendar's day-picking step — see
 * utils/placement.js.
 *
 * @param {(draft: {name: string, repeat: string, sport: object}) => void} onCreate
 * @param {() => void} onClose
 */
export default function SportComposer({ onCreate, onClose }) {
  const [name, setName] = useState('');
  const [repeat, setRepeat] = useState(REPEAT_ONCE);
  const [mode, setMode] = useState(MODE_STATIONS);
  // One draft per mode, so flipping the switch does not throw away your work.
  const [drafts, setDrafts] = useState(() => ({
    [MODE_STATIONS]: createSportConfig(MODE_STATIONS),
    [MODE_SINGLE]: createSportConfig(MODE_SINGLE),
  }));

  const sport = drafts[mode];
  const setSport = (next) => setDrafts((prev) => ({ ...prev, [mode]: next }));

  // Valid means: it has a title and at least one named value to record.
  const valid = useMemo(
    () => Boolean(name.trim()) && Boolean(normalizeSportConfig(sport)),
    [name, sport]
  );

  const patchStation = (id, next) =>
    setSport({ ...sport, stations: sport.stations.map((s) => (s.id === id ? next : s)) });

  const canAddStation = mode === MODE_STATIONS && sport.stations.length < MAX_STATIONS;

  /**
   * Append a station that already carries the previous one's value columns —
   * a second exercise almost always reports the same kg / reps, so starting
   * from a blank row would only mean retyping them.
   */
  const addStation = () => {
    const last = sport.stations[sport.stations.length - 1];
    setSport({ ...sport, stations: [...sport.stations, createStation({ metrics: last?.metrics })] });
  };

  const submit = (event) => {
    event.preventDefault();
    if (!valid) return;
    onCreate({ name: name.trim(), repeat, sport: normalizeSportConfig(sport) });
  };

  return (
    <FullScreenSheet title="new sport" subtitle="design it first, then pick its day" onClose={onClose}>
      <form id="sport-composer" onSubmit={submit} className="flex flex-col gap-5 pb-2">
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="name it — push day, morning run…"
          maxLength={60}
          aria-label="Sport name"
          autoFocus
          className="field-hand field-hand--accent font-hand text-2xl leading-tight
                     placeholder:font-sans placeholder:text-xs placeholder:uppercase
                     placeholder:tracking-widest placeholder:text-ink-soft"
        />

        <PillChoice label="type" options={MODES} value={mode} onChange={setMode} />
        <p className="-mt-3 font-hand text-base text-ink-soft">
          {MODES.find((option) => option.value === mode).hint}
        </p>

        <PillChoice label="repeat" options={REPEATS} value={repeat} onChange={setRepeat} />
        {/* The day is chosen after this screen, so say what tapping one will
            mean while the repeat is still in front of you. */}
        <p className="-mt-3 font-hand text-base text-ink-soft">{placementHint(repeat)}</p>

        <CheckRow
          checked={Boolean(sport.evaluate)}
          onChange={(evaluate) => setSport({ ...sport, evaluate })}
          label="track progress"
          hint="adds a chart of how these numbers move over time"
        />

        {mode === MODE_STATIONS ? (
          <div className="flex flex-col gap-3">
            {sport.stations.map((station, index) => (
              <StationEditor
                key={station.id}
                station={station}
                index={index}
                onChange={(next) => patchStation(station.id, next)}
                // The last remaining station cannot be removed.
                onRemove={
                  sport.stations.length > 1
                    ? () =>
                        setSport({
                          ...sport,
                          stations: sport.stations.filter((s) => s.id !== station.id),
                        })
                    : null
                }
              />
            ))}
            {/* "+ station" lives in the sticky footer — always in thumb reach,
                however long the list of stations grows. */}
          </div>
        ) : (
          // One-step activities skip stations and sets entirely.
          <section className="panel-hand flex flex-col gap-3 p-3">
            <MetricsEditor
              metrics={sport.stations[0].metrics}
              onChange={(metrics) =>
                setSport({ ...sport, stations: [{ ...sport.stations[0], metrics }] })
              }
              hint="e.g. km"
            />
          </section>
        )}
      </form>

      {/* Sticky footer lives outside the scroll area, hence the form attribute */}
      <div className="paper-dots sticky bottom-0 -mx-4 mt-4 flex items-center gap-3 border-t
                      border-dashed border-line px-4 py-3 sm:-mx-6 sm:px-6">
        {/* Closing is the ✕ in the header; the footer slot is worth more as the
            action you actually repeat while building a workout. */}
        {canAddStation && (
          <button
            type="button"
            onClick={addStation}
            className="rounded-pill border border-line px-3 py-1.5 text-[10px] uppercase
                       tracking-widest text-ink-soft transition-colors hover:border-accent
                       hover:text-accent"
          >
            + station
          </button>
        )}

        <button
          type="submit"
          form="sport-composer"
          disabled={!valid}
          className="ml-auto rounded-pill border border-accent bg-accent px-4 py-1.5 text-[10px]
                     uppercase tracking-widest text-white transition-opacity
                     disabled:cursor-not-allowed disabled:opacity-40"
        >
          pick a day
        </button>
      </div>
    </FullScreenSheet>
  );
}
